import React, { useEffect, useState } from "react";
import * as Updates from "expo-updates";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Globe } from "lucide-react-native";
import { useRouter } from "expo-router";

import CheckingUpdatesCard from "./Components/CheckingUpdatesCard";
import DownloadingCard from "./Components/DownloadingCard";
import InstallingCard from "./Components/RestartCard";
import type { CardStatus } from "./Components/BaseCard";
import { showNoUpdatesAvailableAlert, showUpdateErrorAlert } from "./alerts";

import { themes } from "@/Components/Styling/themeConfigs";

function AppUpdates(): React.JSX.Element {
  const currentTheme = themes.morning;

  const router = useRouter();
  const [checkingStatus, setCheckingStatus] = useState<CardStatus>("pending");
  const [downloadingStatus, setDownloadingStatus] =
    useState<CardStatus>("pending");
  const [installingStatus, setInstallingStatus] =
    useState<CardStatus>("pending");

  const onRetry = async (): Promise<void> => {
    setDownloadingStatus("pending");
    setInstallingStatus("pending");
    await checkForUpdates();
  };

  const onCancel = (): void => {
    router.push("/");
  };

  const checkForUpdates = async (): Promise<void> => {
    setCheckingStatus("active");
    try {
      const update = await Updates.checkForUpdateAsync();
      if (update.isAvailable) {
        await downloadUpdate();
      } else {
        showNoUpdatesAvailableAlert();
      }
    } catch (error) {
      showUpdateErrorAlert(onRetry, onCancel);
    } finally {
      setCheckingStatus("completed");
    }
  };
  const downloadUpdate = async (): Promise<void> => {
    setDownloadingStatus("active");
    try {
      const result = await Updates.fetchUpdateAsync();
      await Updates.reloadAsync();
      if (result.isNew) {
        setDownloadingStatus("completed");
        setInstallingStatus("completed");
      } else {
        onCancel();
      }
    } catch (error) {
      showUpdateErrorAlert(onRetry, onCancel);
    } finally {
      setDownloadingStatus("completed");
    }
  };

  const handleRestart = async (): Promise<void> => {
    setInstallingStatus("active");
    try {
      await Updates.reloadAsync();
    } catch (error) {
      showUpdateErrorAlert(onRetry, onCancel);
    } finally {
      setInstallingStatus("completed");
    }
  };

  useEffect(() => {
    checkForUpdates();
  }, []);

  return (
    <LinearGradient
      colors={currentTheme.bg}
      style={styles.container}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <Text style={[styles.title, { color: currentTheme.text }]}>
        Kai Social
      </Text>
      <View style={styles.taglineContainer}>
        <Globe color={currentTheme.text} size={20} />
        <Text style={[styles.tagline, { color: currentTheme.text }]}>
          Explore. Connect. Share.
        </Text>
      </View>
      <View style={styles.updateCardsContainer}>
        <CheckingUpdatesCard status={checkingStatus} />
        <DownloadingCard status={downloadingStatus} />
        <TouchableOpacity onPress={handleRestart}>
          <InstallingCard status={installingStatus} />
        </TouchableOpacity>
      </View>
      {installingStatus === "completed" && (
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={styles.restartButton}
            onPress={handleRestart}
          >
            <Text
              style={[styles.restartButtonText, { color: currentTheme.text }]}
            >
              Restart Now
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: 48,
    fontWeight: "bold",
    marginBottom: 8,
  },
  updateCardsContainer: {
    width: "100%",
    maxWidth: 400,
    marginTop: 20,
  },
  tagline: {
    fontSize: 20,
    left: 5,
  },
  taglineContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  buttonContainer: {
    marginTop: 32,
    width: "100%",
    maxWidth: 400,
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
  },
  restartButton: {
    maxWidth: "50%",
    backgroundColor: "white",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    flexGrow: 1,
    alignItems: "center",
  },
  restartButtonText: {
    color: "white",
    fontWeight: "600",
    fontSize: 16,
  },
});

export default AppUpdates;
